'use client'
import { useState, useEffect, useRef } from 'react';
import { loginMember, saveMemberLoginSession, getCurrentMemberLogin, logoutMember } from '@/services/memberLoginApi';
import { getPropertyCode } from '@/utils/propertyManager';
import turnstileConfig from '@/config/turnstile';

const MemberLoginModal = ({ isOpen, onClose, onLoginSuccess }) => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [turnstileToken, setTurnstileToken] = useState('');
  const [turnstileLoaded, setTurnstileLoaded] = useState(false);
  const turnstileWidgetRef = useRef(null);

  // Clear form when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setEmail('');
      setPassword('');
      setError('');
      setTurnstileToken('');
      
      // Load Turnstile script when modal opens
      if (!window.turnstile) {
        const script = document.createElement('script');
        script.src = turnstileConfig.scriptUrl;
        script.async = true;
        script.onload = () => {
          setTurnstileLoaded(true);
        };
        document.body.appendChild(script);
        
        return () => {
          // Clean up script if modal closes before script loads
          if (document.body.contains(script)) {
            document.body.removeChild(script);
          }
        };
      } else {
        setTurnstileLoaded(true);
      }
    }
  }, [isOpen]);
  
  // Initialize Turnstile widget when script is loaded
  useEffect(() => {
    if (isOpen && turnstileLoaded && window.turnstile) {
      // Reset any existing widget
      if (turnstileWidgetRef.current) {
        window.turnstile.reset(turnstileWidgetRef.current);
      }
      
      // Render the widget
      turnstileWidgetRef.current = window.turnstile.render('#turnstile-widget', {
        sitekey: turnstileConfig.siteKey,
        callback: (token) => {
          setTurnstileToken(token);
        },
        'expired-callback': () => {
          setTurnstileToken('');
        },
        'error-callback': () => {
          setError('Turnstile verification failed. Please try again.');
          setTurnstileToken('');
        },
        theme: 'light',
      });
      
      return () => {
        // Clean up widget when component unmounts
        if (turnstileWidgetRef.current) {
          window.turnstile.reset(turnstileWidgetRef.current);
        }
      };
    }
  }, [isOpen, turnstileLoaded]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    setError('');

    // Check if Turnstile token is available
    if (!turnstileToken && !turnstileConfig.isDevelopment) {
      setError('Please complete the security verification');
      setIsLoading(false);
      return;
    }

    try {
      // Get current property code
      const propertyCode = getPropertyCode();
      
      // Attempt login with Turnstile token
      const loginResponse = await loginMember(email, password, propertyCode, turnstileToken);

      if (loginResponse.success) {
        // Save login session
        saveMemberLoginSession(loginResponse.data);
        
        // Call success callback
        if (onLoginSuccess) {
          onLoginSuccess(loginResponse.data);
        }
        
        // Close modal
        onClose();
        
        // Show success message
        console.log('Member login successful:', loginResponse.data);
      } else {
        setError(loginResponse.message || 'Login failed');
      }
    } catch (error) {
      console.error('Login error:', error);
      setError(error.message || 'An error occurred during login');
    } finally {
      setIsLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
          <h2 className="text-xl font-semibold text-gray-900">Member Login</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 transition-colors"
          >
            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Body */}
        <div className="p-6">
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                placeholder="Enter your email"
                required
                disabled={isLoading}
              />
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <input
                  type={showPassword ? 'text' : 'password'}
                  id="password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full px-3 py-2 pr-10 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  placeholder="Enter your password"
                  required
                  disabled={isLoading}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
                  disabled={isLoading}
                >
                  {showPassword ? (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                    </svg>
                  ) : (
                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                    </svg>
                  )}
                </button>
              </div>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-md p-3">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-red-700">{error}</p>
                  </div>
                </div>
              </div>
            )}
            
            {/* Turnstile Widget */}
            <div className="mt-4">
              <div id="turnstile-widget" className="flex justify-center"></div>
              {!turnstileLoaded && (
                <div className="text-center text-sm text-gray-500 mt-2">
                  Loading security verification...
                </div>
              )}
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading || !email || !password}
              className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {isLoading ? (
                <div className="flex items-center">
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Signing In...
                </div>
              ) : (
                'Sign In'
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="mt-6">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-gray-300" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white text-gray-500">Don't have an account?</span>
              </div>
            </div>
          </div>

          {/* Register Link */}
          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={() => {
                const propertyCode = getPropertyCode() || 'DEMO';
                const registerUrl = `https://member.emerald-system.id/register?propertycode=${propertyCode}`;
                window.open(registerUrl, '_blank');
              }}
              className="text-blue-600 hover:text-blue-500 text-sm font-medium"
            >
              Register as a new member
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MemberLoginModal;